import { SAVED_BOARD_FILTERS_MAX, canEnterGatedPhase } from '@projektverwaltung/shared';
import type {
  BefundUebersichtEintrag,
  BoardFilters,
  SavedBoardFilter,
} from '@projektverwaltung/shared';
/**
 * V1.5 Phase Board — drei Linsen auf dieselben Daten.
 * Quelle: Implementation Brief §7.4 + Screen-Inventory §5.
 *
 *   Pipeline · Kanban · Matrix
 *
 * Switch über segmentierten Pill-Toggle im Header. Die aktive Linse liegt seit
 * Issue #63 am Konto (`view_filters.board.lens`, geräteübergreifend); der alte
 * localStorage-Wert (pv:phase-lens) wird nur noch einmalig übernommen.
 * Default-Filter pro Persona (PL=lead=me, BL=segment=mein) folgt mit Phase 9.
 *
 * DnD im Kanban: Phase 6.5 (separater Subschritt, ruft POST /v1/projects/:id/
 * move-phase auf).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/toast.js';
import { BefundMarker, useBefundUebersicht } from '../../components/v2/befunde/befund-marker.js';
import { bereichColors } from '../../components/v2/bereich-badge.js';
import { BoardPflichtfeldDialog } from '../../components/v2/board-pflichtfeld-dialog.js';
import { DueDateBadge } from '../../components/v2/due-date-badge.js';
import {
  Chip,
  ChipSelect,
  DueRangeFilter,
  MultiSelectPicker,
  NO_TAGS_VALUE,
  PersonPicker,
  SearchInput,
  Toggle,
} from '../../components/v2/filters/filter-primitives.js';
import { GespeicherteFilter } from '../../components/v2/gespeicherte-filter.js';
import { InfoKnopf } from '../../components/v2/hilfe/info-knopf.js';
import { ApiError, api } from '../../lib/api.js';
import { useFeatureFlag } from '../../lib/features.js';
import { personInitials, shortPersonName } from '../../lib/person-name.js';
import {
  type PersonaSegmente,
  personaPraedikat,
  personaSegmente,
} from '../../lib/persona-scope.js';
import { usePersona } from '../../lib/persona.js';
import {
  type DialogFehler,
  anlegen,
  bereinige,
  loeschen,
  ueberschreiben,
  umbenennen,
} from '../../lib/saved-filters.js';
import { useSession } from '../../lib/session.js';
import { PREFERENCES_QUERY_KEY, resolveStartValue } from '../../lib/sticky-filters.js';
import { useSavedBoardFilters } from '../../lib/use-saved-filters.js';
import { useStickyFilters } from '../../lib/use-sticky-filters.js';
import { kanbanColumnTemplate, matrixColumnTemplate } from './phase-board-layout.js';
import { matchesTagFilter, resolveTagFilterStart } from './phase-board-tag-filter.js';

const DND_MIME = 'application/x-pv-project-id';

type ProjectTag = {
  tag_id: string;
  name: string;
  color: string | null;
  manager_user_id?: string | null;
};

type Project = {
  project_id: string;
  project_key: string;
  name: string;
  customer_name: string;
  segment: string;
  phase_id: string;
  phase_changed_at: string;
  lifecycle_status: import('@projektverwaltung/shared').LifecycleStatus;
  staffing_needed: boolean;
  is_blocked?: boolean;
  blocked_reason?: string | null;
  qs_with_bl_open?: boolean;
  project_lead_user_id?: string | null;
  manager_user_id?: string | null;
  mtb_user_id?: string | null;
  responsibility?: 'PU' | 'Service' | 'MTB' | null;
  ms_classification?: 'msPeople' | 'msSolution' | 'msService' | null;
  due_date?: string | null;
  tags?: ProjectTag[];
};

type Phase = { phase_id: string; name: string; order: number };

const SEGMENTS = ['KI', 'IT', 'ProLo', 'V&S', 'HR'];
const RESPONSIBILITIES = ['PU', 'Service', 'MTB'];
const MS_CLASSES = ['msPeople', 'msSolution', 'msService'];

type Lens = 'pipeline' | 'kanban' | 'matrix';

const LENS_STORAGE_KEY = 'pv:phase-lens';
const DAY_MS = 86_400_000;

/**
 * Zuletzt lokal gemerkte Linse. Seit #63 liegt die Ansichtswahl am Konto
 * (`view_filters.board.lens`); dieser Wert dient nur noch als Startwert vor dem
 * Laden der Präferenzen und als einmalige Übernahmequelle für Bestandsnutzer.
 */
function readLocalLens(): Lens | undefined {
  const stored = window.localStorage.getItem(LENS_STORAGE_KEY);
  return stored === 'kanban' || stored === 'matrix' || stored === 'pipeline' ? stored : undefined;
}

export function PhaseBoardPage() {
  const { t } = useTranslation();
  const { user } = useSession();
  const persona = usePersona();
  const prefsQuery = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: () => api.getMyPreferences(),
  });
  const defaultLens = prefsQuery.data?.preferences.default_phase_lens;
  const [localLens] = useState<Lens | undefined>(readLocalLens);
  const [lens, setLens] = useState<Lens>(() => localLens ?? 'pipeline');
  /**
   * true, sobald eine echte Ansichtswahl vorliegt — gemerkt am Konto, einmalig aus
   * dem localStorage übernommen oder gerade umgeschaltet. NUR dann wandert `lens`
   * in den Sticky-Snapshot.
   *
   * Ohne diese Unterscheidung schriebe jeder Seitenaufruf die Standard-Linse aus
   * den Einstellungen ans Konto und löschte damit die Wahl, die auf einem anderen
   * Gerät getroffen wurde. Umgekehrt bleibt `default_phase_lens` für alle, die nie
   * umgeschaltet haben, ein lebender Standard (Änderung in den Einstellungen greift
   * weiter), statt beim ersten Aufruf still zur „Wahl" zu werden.
   */
  const [lensChosen, setLensChosen] = useState(false);
  /** Verhindert, dass der Startwert nach der Hydration ein zweites Mal greift. */
  const lensStartApplied = useRef(false);
  const applyStartLens = useCallback(
    (remembered: Lens | undefined) => {
      lensStartApplied.current = true;
      // Der Nutzer kann schneller sein als die Präferenz-Anfrage. Hat er schon
      // umgeschaltet, gewinnt seine Wahl: ohne diesen Riegel würde sie hier
      // entweder durch `default_phase_lens` ersetzt oder still zu „keine Wahl"
      // degradiert (weiter angezeigt, aber nie gespeichert).
      if (lensChosen) return;
      const start = resolveStartValue(remembered, defaultLens);
      if (start.value) setLens(start.value);
      setLensChosen(start.chosen);
    },
    [defaultLens, lensChosen],
  );
  const [defaultFilterActive, setDefaultFilterActive] = useState(true);
  const [overdueOnly, setOverdueOnly] = useState(false);
  // PV-052: nur Projekte mit Befund-Marker. Ohne Flag `befunde` weder sichtbar noch wirksam —
  // die Übersicht ist dann leer, der Filter würde sonst jedes Projekt ausblenden.
  const befundeAn = useFeatureFlag('befunde');
  const befundUebersicht = useBefundUebersicht();
  const [befundOnlyGewaehlt, setBefundOnly] = useState(false);
  const befundOnly = befundeAn && befundOnlyGewaehlt;
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  // Atlas-paritätischer Filter-Satz (F4)
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState<string | null>(null);
  const [bereichFilter, setBereichFilter] = useState<string | null>(null);
  const [msFilter, setMsFilter] = useState<string | null>(null);
  const [needsOnly, setNeedsOnly] = useState(false);
  const [blockedOnly, setBlockedOnly] = useState(false);
  // 'assigned' | 'unassigned' | <konkrete mtb_user_id> | null
  const [mtbFilter, setMtbFilter] = useState<string | null>(null);
  // Zugewiesene Projektleiter (project_lead_user_id) — Mehrfachauswahl. Leeres
  // Array = kein Filter; sonst matcht ein Projekt, wenn sein PL in der Auswahl ist.
  const [plFilter, setPlFilter] = useState<string[]>([]);
  // Tags — Mehrfachauswahl (#81). Leeres Array = kein Filter; sonst matcht ein Projekt,
  // wenn es MINDESTENS EINEN der gewählten Tags trägt. `NO_TAGS_VALUE` steht in derselben
  // Liste und matcht Projekte ganz ohne Tag.
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  // Konkrete Person: matcht als Tag-Verantwortliche:r (tags.manager_user_id) ODER
  // als SF-Fachbereichsleiter (projects.manager_user_id). null = kein Filter.
  const [managerFilter, setManagerFilter] = useState<string | null>(null);

  // Hidden-Phases pro User (F4) — persistiert in users.preferences.hidden_phase_ids.
  const queryClientPrefs = useQueryClient();
  const hiddenPhaseIds = prefsQuery.data?.preferences.hidden_phase_ids ?? [];
  const setHiddenPhases = useMutation({
    mutationFn: (ids: string[]) => api.updateMyPreferences({ hidden_phase_ids: ids }),
    onSuccess: (res) => {
      queryClientPrefs.setQueryData(PREFERENCES_QUERY_KEY, res);
      void queryClientPrefs.invalidateQueries({ queryKey: PREFERENCES_QUERY_KEY });
    },
  });
  const togglePhaseHidden = (phaseId: string) => {
    const next = hiddenPhaseIds.includes(phaseId)
      ? hiddenPhaseIds.filter((id) => id !== phaseId)
      : [...hiddenPhaseIds, phaseId];
    setHiddenPhases.mutate(next);
  };

  // EIN Schnappschuss der Board-Filter — gemerkte Filter (Sticky) und gespeicherte Filter (#141)
  // bauen darauf auf, damit beide dieselben Felder kennen. Bewusst OHNE Freitext-Suche.
  const filterStand: BoardFilters = {
    persona_default_active: defaultFilterActive,
    segment: segmentFilter,
    bereich: bereichFilter,
    ms: msFilter,
    mtb: mtbFilter,
    pl: plFilter,
    tags: tagFilter,
    manager: managerFilter,
    needs_only: needsOnly,
    blocked_only: blockedOnly,
    overdue_only: overdueOnly,
    befund_only: befundOnlyGewaehlt,
    due_from: dueFrom,
    due_to: dueTo,
  };
  // Gemeinsames „in den State schreiben" für Sticky-Hydration und gespeicherte Filter. Ein
  // fehlender Schlüssel (`undefined`) lässt den jeweiligen Filter unangetastet.
  const setzeFilter = (bf: BoardFilters) => {
    if (bf.persona_default_active !== undefined) {
      setDefaultFilterActive(bf.persona_default_active);
    }
    if (bf.segment !== undefined) setSegmentFilter(bf.segment ?? null);
    if (bf.bereich !== undefined) setBereichFilter(bf.bereich ?? null);
    if (bf.ms !== undefined) setMsFilter(bf.ms ?? null);
    if (bf.mtb !== undefined) setMtbFilter(bf.mtb ?? null);
    if (bf.pl !== undefined) setPlFilter(bf.pl ?? []);
    // Vorbelegung + Altbestands-Übernahme stecken in resolveTagFilterStart (#81) —
    // dort liegt die Reihenfolge einmal, damit sie sich nicht je Seite verdrehen lässt.
    const tagStart = resolveTagFilterStart(bf);
    if (tagStart !== undefined) setTagFilter(tagStart);
    if (bf.manager !== undefined) setManagerFilter(bf.manager ?? null);
    if (bf.needs_only !== undefined) setNeedsOnly(bf.needs_only);
    if (bf.blocked_only !== undefined) setBlockedOnly(bf.blocked_only);
    if (bf.overdue_only !== undefined) setOverdueOnly(bf.overdue_only);
    if (bf.befund_only !== undefined) setBefundOnly(bf.befund_only);
    if (bf.due_from !== undefined) setDueFrom(bf.due_from);
    if (bf.due_to !== undefined) setDueTo(bf.due_to);
  };

  // Sticky Filters (geräteübergreifend): zuletzt gesetzte Filter merken und beim
  // Laden vorbelegen. Die Mechanik steckt vollständig in useStickyFilters
  // (lib/sticky-filters.ts) — hier steht nur noch, WAS gemerkt wird.
  // Der Persona-Standardsicht-Toggle (defaultFilterActive) wird mitgemerkt — die
  // Standardsicht wird also vom letzten Stand überschrieben und kann jederzeit über die
  // Persona-Schnellauswahl wieder aktiviert werden.
  const sticky = useStickyFilters('board', {
    values: {
      ...filterStand,
      // Rollback-Brücke (#81): Ein einzelner echter Tag bleibt auch für eine
      // zurückgerollte Frontend-Version lesbar. Eine Mehrfachauswahl lässt sich hier
      // nicht abbilden → null. Kostet ein Feld im JSON und rettet im Ernstfall die
      // Einstellung des Nutzers.
      tag: tagFilter.length === 1 && tagFilter[0] !== NO_TAGS_VALUE ? tagFilter[0] : null,
      // `lens` fehlt im Snapshot, solange keine Wahl getroffen wurde (siehe
      // lensChosen) — ein fehlender Schlüssel wird beim Merge nicht geschrieben.
      //
      // ACHTUNG, geschützte Stelle: fällt die Bedingung weg, schreibt jeder
      // Seitenaufruf die Standard-Linse ans Konto und löscht damit die auf einem
      // anderen Gerät getroffene Wahl.
      //
      // Seit Issue #64 gibt es dafür ein Netz: `phase-board-sticky.test.tsx`
      // rendert diese Seite echt (jsdom + Testing-Library) und wird rot, wenn die
      // Bedingung hier verschwindet. Der ältere Test in `sticky-filters.test.ts`
      // („Phase Board — gemerkte Ansichtswahl vs. default_phase_lens", `mountBoard`)
      // bleibt als Modell der reinen Regel bestehen — er allein würde den Verlust
      // NICHT bemerken.
      ...(lensChosen ? { lens } : {}),
    },
    // Bestandsnutzer: die bisher nur lokal gemerkte Linse einmalig ans Konto ziehen.
    local: { lens: localLens },
    apply: (bf) => {
      setzeFilter(bf);
      // Die gemerkte Ansichtswahl gewinnt gegen die Standard-Linse aus den
      // Einstellungen — die ist nur Startwert, solange am Konto nichts steht.
      applyStartLens(bf.lens);
    },
  });

  // Greift für den Fall, dass gar nichts vorzubelegen war (weder am Konto noch
  // lokal): dann ruft der Hook `apply` nicht auf, der Startwert muss trotzdem
  // gesetzt werden.
  useEffect(() => {
    if (!sticky.hydrated || lensStartApplied.current) return;
    applyStartLens(undefined);
  }, [sticky.hydrated, applyStartLens]);

  // Aktive (Nicht-Persona-)Filter — für Indikator + „Alle Filter entfernen".
  // Freitext zählt in-Session mit, wird aber nicht persistiert.
  const activeFilterCount =
    (segmentFilter ? 1 : 0) +
    (bereichFilter ? 1 : 0) +
    (msFilter ? 1 : 0) +
    (mtbFilter ? 1 : 0) +
    (plFilter.length > 0 ? 1 : 0) +
    (tagFilter.length > 0 ? 1 : 0) +
    (managerFilter ? 1 : 0) +
    (needsOnly ? 1 : 0) +
    (blockedOnly ? 1 : 0) +
    (overdueOnly ? 1 : 0) +
    (befundOnly ? 1 : 0) +
    (dueFrom ? 1 : 0) +
    (dueTo ? 1 : 0) +
    (search.trim() ? 1 : 0);
  // „Alle Filter entfernen": setzt alle Filter zurück — die Persona-Standardsicht
  // (defaultFilterActive) bleibt bewusst unangetastet.
  const resetFilters = () => {
    setSegmentFilter(null);
    setBereichFilter(null);
    setMsFilter(null);
    setMtbFilter(null);
    setPlFilter([]);
    setTagFilter([]);
    setManagerFilter(null);
    setNeedsOnly(false);
    setBlockedOnly(false);
    setOverdueOnly(false);
    setBefundOnly(false);
    setDueFrom('');
    setDueTo('');
    setSearch('');
  };

  // #81: Die frühere Sperre `canFilterByTag` (blendete den Filter für System-Admins und
  // die Kaufm. Leitung aus, Begründung „globale Übersichtssicht") ist ersatzlos entfallen.
  // Sie unterstellte, dass diese Rollen keinen Themenschnitt brauchen — das Gegenteil
  // stimmt: Je größer die Menge, desto nötiger der Schnitt. Der Filter verbirgt nur, er
  // zeigt nichts Neues, öffnet also keinen Rechte-Pfad. Übrig bleibt die reine
  // Datenbedingung `options.length > 0`, die der Picker selbst prüft.

  const projectsQuery = useQuery({
    queryKey: ['v2-board-projects'],
    queryFn: () => api.listProjects({}) as Promise<{ items: Project[] }>,
  });
  const phasesQuery = useQuery({ queryKey: ['v2-board-phases'], queryFn: () => api.listPhases() });
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });

  // Gespeicherte Filter (#141) — persönlich, nur hier im Phase Board.
  const toast = useToast();
  const gespeichert = useSavedBoardFilters();
  const tagsQuery = useQuery({ queryKey: ['tags-all'], queryFn: () => api.listTags() });
  /** Schnappschuss zum Speichern: alle Filter plus die gerade angezeigte Linse. */
  const gespeicherterStand = (): BoardFilters => ({ ...filterStand, lens });
  const speichereListe = async (liste: SavedBoardFilter[]): Promise<boolean> => {
    try {
      await gespeichert.speichere(liste);
      return true;
    } catch (err) {
      toast.error(
        t('v2.board.saved_failed', 'Gespeicherte Filter konnten nicht gesichert werden'),
        err instanceof Error ? err.message : undefined,
      );
      return false;
    }
  };
  const wendeGespeichertAn = (f: SavedBoardFilter) => {
    // Werte, die es nicht mehr gibt (gelöschter Tag, inaktive Person), werden übersprungen.
    // Solange Nutzer oder Tags noch laden, wird nichts bereinigt — sonst fiele alles weg.
    const bekannt =
      usersQuery.data && tagsQuery.data
        ? {
            userIds: new Set(
              usersQuery.data.items.filter((u) => u.active !== false).map((u) => u.user_id),
            ),
            tagIds: new Set(tagsQuery.data.items.map((tg) => tg.tag_id)),
          }
        : null;
    const { filters, entfernt } = bekannt
      ? bereinige(f.filters, bekannt)
      : { filters: f.filters, entfernt: 0 };
    // Vollständig setzen: was im gespeicherten Filter fehlt, ist „aus" — nicht „wie gerade".
    setzeFilter({
      persona_default_active: filters.persona_default_active ?? true,
      segment: filters.segment ?? null,
      bereich: filters.bereich ?? null,
      ms: filters.ms ?? null,
      mtb: filters.mtb ?? null,
      pl: filters.pl ?? [],
      tags: filters.tags ?? [],
      manager: filters.manager ?? null,
      needs_only: filters.needs_only ?? false,
      blocked_only: filters.blocked_only ?? false,
      overdue_only: filters.overdue_only ?? false,
      befund_only: filters.befund_only ?? false,
      due_from: filters.due_from ?? '',
      due_to: filters.due_to ?? '',
    });
    setSearch('');
    if (filters.lens) {
      setLensChosen(true);
      setLens(filters.lens);
    }
    toast.success(
      t('v2.board.saved_applied', 'Filter „{{name}}" angewendet', { name: f.name }),
      entfernt > 0
        ? t('v2.board.saved_stale', '{{count}} Filterwert gibt es nicht mehr', { count: entfernt })
        : undefined,
    );
  };
  const gespeicherteFilterLeiste = (
    <GespeicherteFilter
      liste={gespeichert.liste}
      bereit={gespeichert.bereit}
      voll={gespeichert.liste.length >= SAVED_BOARD_FILTERS_MAX}
      onAufrufen={wendeGespeichertAn}
      onAnlegen={async (name): Promise<DialogFehler | null> => {
        const r = anlegen(gespeichert.liste, {
          id: crypto.randomUUID(),
          name,
          filters: gespeicherterStand(),
          jetzt: new Date(),
        });
        if (!r.ok) return r.grund;
        if (!(await speichereListe(r.liste))) return 'speichern_fehlgeschlagen';
        toast.success(t('v2.board.saved_created', 'Filter gespeichert'));
        return null;
      }}
      onUmbenennen={async (id, name) => {
        const r = umbenennen(gespeichert.liste, id, name);
        if (!r.ok) return r.grund;
        return (await speichereListe(r.liste)) ? null : 'speichern_fehlgeschlagen';
      }}
      onUeberschreiben={async (id) => {
        if (await speichereListe(ueberschreiben(gespeichert.liste, id, gespeicherterStand()))) {
          toast.success(t('v2.board.saved_overwritten', 'Filter überschrieben'));
        }
      }}
      onLoeschen={async (id) => {
        await speichereListe(loeschen(gespeichert.liste, id));
      }}
    />
  );

  const allActive = (projectsQuery.data?.items ?? []).filter(
    (p) => p.lifecycle_status === 'active',
  );
  const phases = (phasesQuery.data?.items ?? []).slice().sort((a, b) => a.order - b.order);

  const filter = computePersonaFilter(
    persona.active,
    user?.user_id,
    personaSegmente(user?.function_roles ?? []),
    t as never,
  );
  const personaFiltered =
    defaultFilterActive && filter ? allActive.filter(filter.predicate) : allActive;
  const todayIso = new Date().toISOString().slice(0, 10);
  const projects = personaFiltered.filter((p) => {
    if (overdueOnly) {
      if (!p.due_date || p.lifecycle_status !== 'active' || p.due_date >= todayIso) return false;
    }
    if (befundOnly && !befundUebersicht.has(p.project_id)) return false;
    if (dueFrom && (!p.due_date || p.due_date < dueFrom)) return false;
    if (dueTo && (!p.due_date || p.due_date > dueTo)) return false;
    if (segmentFilter && p.segment !== segmentFilter) return false;
    if (bereichFilter && p.responsibility !== bereichFilter) return false;
    if (msFilter && p.ms_classification !== msFilter) return false;
    if (needsOnly && !p.staffing_needed) return false;
    if (blockedOnly && !p.is_blocked) return false;
    if (mtbFilter === 'assigned' && !p.mtb_user_id) return false;
    if (mtbFilter === 'unassigned' && p.mtb_user_id) return false;
    if (
      mtbFilter &&
      mtbFilter !== 'assigned' &&
      mtbFilter !== 'unassigned' &&
      p.mtb_user_id !== mtbFilter
    )
      return false;
    // PL-Mehrfachfilter: Projekt matcht, wenn sein zugewiesener Projektleiter in
    // der Auswahl ist (Projekte ohne PL fallen bei aktivem Filter raus).
    if (
      plFilter.length > 0 &&
      (!p.project_lead_user_id || !plFilter.includes(p.project_lead_user_id))
    )
      return false;
    if (!matchesTagFilter(p.tags, tagFilter)) return false;
    // Manager-Filter „beide zusammen": Projekt matcht, wenn die gewählte Person
    // SF-Fachbereichsleiter (manager_user_id) ODER Tag-Verantwortliche:r ist.
    if (
      managerFilter &&
      p.manager_user_id !== managerFilter &&
      !(p.tags ?? []).some((tg) => tg.manager_user_id === managerFilter)
    )
      return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = `${p.project_key} ${p.customer_name} ${p.name} ${p.segment}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Sichtbare Phasen (ausgeblendete entfernen) — wirkt auf alle Linsen.
  const visiblePhases = phases.filter((ph) => !hiddenPhaseIds.includes(ph.phase_id));
  // Tag-Optionen aus den geladenen Projekten. Alphabetisch sortiert (wie mtbOptions):
  // Bei Mehrfachauswahl ist Wiederfinden wichtiger als die Einfügereihenfolge.
  // „Keine Tags" steht vorn und wird nur angehängt, wenn es überhaupt echte Tags gibt —
  // damit bleibt „Filter unsichtbar im tag-freien Bestand" allein eine Frage von
  // `options.length`, ohne zusätzliche Bedingung an der Aufrufstelle.
  const tagOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allActive) for (const tg of p.tags ?? []) map.set(tg.tag_id, tg.name);
    const echte = Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (echte.length === 0) return echte;
    return [{ value: NO_TAGS_VALUE, label: t('v2.board.filter_tag_none', 'Keine Tags') }, ...echte];
  }, [allActive, t]);
  // Konkrete MTBs: alle als MTB zugewiesenen User aus den aktiven Projekten (F4/Test-Befund 2).
  const mtbOptions = useMemo(() => {
    const nameById = new Map(
      (usersQuery.data?.items ?? []).map((u) => [u.user_id, u.name] as const),
    );
    const ids = new Set<string>();
    for (const p of allActive) if (p.mtb_user_id) ids.add(p.mtb_user_id);
    return Array.from(ids)
      .map((id) => ({ value: id, label: nameById.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allActive, usersQuery.data]);
  // Zugewiesene Projektleiter: alle in aktiven Projekten als PL gesetzten Personen
  // (project_lead_user_id), dedupliziert, nach Name sortiert.
  const plOptions = useMemo(() => {
    const nameById = new Map(
      (usersQuery.data?.items ?? []).map((u) => [u.user_id, u.name] as const),
    );
    const ids = new Set<string>();
    for (const p of allActive) if (p.project_lead_user_id) ids.add(p.project_lead_user_id);
    return Array.from(ids)
      .map((id) => ({ value: id, label: nameById.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allActive, usersQuery.data]);
  // Konkrete Manager: alle Personen, die in aktiven Projekten entweder als
  // SF-Fachbereichsleiter (manager_user_id) oder als Tag-Verantwortliche:r
  // (tags.manager_user_id) vorkommen — Vereinigung, dedupliziert, nach Name sortiert.
  const managerOptions = useMemo(() => {
    const nameById = new Map(
      (usersQuery.data?.items ?? []).map((u) => [u.user_id, u.name] as const),
    );
    const ids = new Set<string>();
    for (const p of allActive) {
      if (p.manager_user_id) ids.add(p.manager_user_id);
      for (const tg of p.tags ?? []) if (tg.manager_user_id) ids.add(tg.manager_user_id);
    }
    return Array.from(ids)
      .map((id) => ({ value: id, label: nameById.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allActive, usersQuery.data]);
  // #99: Namen für die Projektleitungs-Zeile auf den Karten (Pipeline + Kanban).
  const userNameById = useMemo<ReadonlyMap<string, string>>(
    () => new Map((usersQuery.data?.items ?? []).map((u) => [u.user_id, u.name] as const)),
    [usersQuery.data],
  );

  const byPhase = useMemo(() => {
    const map = new Map<string, Project[]>();
    for (const ph of visiblePhases) map.set(ph.phase_id, []);
    for (const p of projects) {
      const arr = map.get(p.phase_id);
      if (arr) arr.push(p);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, visiblePhases]);

  // #50: Breiter Deckel (--canvas-wide, 1920px) statt --canvas-max. Er gilt fuer
  // die ganze Seite, nicht nur fuer die Linsen: Kopfzeile und Filterleiste sitzen
  // im selben Container, ein engerer Deckel nur um die Linsen herum wuerde die
  // Seite optisch zerreissen. Alle anderen Sichten bleiben bei --canvas-max.
  return (
    <div className="max-w-[var(--canvas-wide)] mx-auto px-2 pb-12 space-y-6">
      <Header
        lens={lens}
        onLens={(l) => {
          // Aktives Umschalten = die Wahl. Ab hier gilt sie geräteübergreifend und
          // wird von der Standard-Linse nicht mehr verdrängt.
          setLensChosen(true);
          setLens(l);
        }}
      />

      {filter && (
        <PersonaFilterChip
          label={filter.label}
          active={defaultFilterActive}
          onToggle={() => setDefaultFilterActive((v) => !v)}
          visibleCount={projects.length}
          totalCount={allActive.length}
        />
      )}

      {gespeicherteFilterLeiste}

      <BoardFilterBar
        open={filterOpen}
        onToggleOpen={() => setFilterOpen((v) => !v)}
        activeFilterCount={activeFilterCount}
        onResetFilters={resetFilters}
        search={search}
        onSearch={setSearch}
        segmentFilter={segmentFilter}
        onSegment={setSegmentFilter}
        bereichFilter={bereichFilter}
        onBereich={setBereichFilter}
        msFilter={msFilter}
        onMs={setMsFilter}
        needsOnly={needsOnly}
        onNeedsOnly={setNeedsOnly}
        blockedOnly={blockedOnly}
        onBlockedOnly={setBlockedOnly}
        overdueOnly={overdueOnly}
        onOverdueOnly={setOverdueOnly}
        befundeAn={befundeAn}
        befundOnly={befundOnly}
        onBefundOnly={setBefundOnly}
        mtbFilter={mtbFilter}
        onMtbFilter={setMtbFilter}
        mtbOptions={mtbOptions}
        plFilter={plFilter}
        onPlToggle={(id) =>
          setPlFilter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
        }
        onPlClear={() => setPlFilter([])}
        plOptions={plOptions}
        managerFilter={managerFilter}
        onManagerFilter={setManagerFilter}
        managerOptions={managerOptions}
        tagFilter={tagFilter}
        onTagToggle={(id) =>
          // Funktional, aus demselben Grund wie beim PL-Filter darüber: Ein Update auf
          // Basis des gerenderten `tagFilter` würde bei zügigen Klicks die vorherige
          // Auswahl ersetzen statt ergänzen.
          setTagFilter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
        }
        onTagClear={() => setTagFilter([])}
        tagOptions={tagOptions}
        dueFrom={dueFrom}
        onDueFrom={setDueFrom}
        dueTo={dueTo}
        onDueTo={setDueTo}
        phases={phases}
        hiddenPhaseIds={hiddenPhaseIds}
        onTogglePhase={togglePhaseHidden}
      />

      {lens === 'pipeline' && (
        <PipelineLens phases={visiblePhases} byPhase={byPhase} userNameById={userNameById} />
      )}
      {lens === 'kanban' && (
        <KanbanLens
          phases={visiblePhases}
          byPhase={byPhase}
          gate={phasesQuery.data?.gate}
          userNameById={userNameById}
        />
      )}
      {lens === 'matrix' && <MatrixLens phases={visiblePhases} projects={projects} />}
    </div>
  );
}

// ===========================================================================
// Persona-Default-Filter (Phase 9)
// ===========================================================================

function computePersonaFilter(
  persona: ReturnType<typeof usePersona>['active'],
  myUserId: string | undefined,
  segmente: PersonaSegmente,
  t: (key: string, fallback?: string, opts?: Record<string, unknown>) => string,
): { label: string; predicate: (p: Project) => boolean } | null {
  // #140: Welche Projekte dazugehören, entscheidet `personaPraedikat` — dieselbe Definition
  // wie in Mission Control. Hier entsteht nur noch das Label des Chips.
  const predicate = personaPraedikat(persona, myUserId, segmente);
  if (!predicate) return null;
  const seg = (liste: string[]) => liste.join(' · ');
  switch (persona) {
    case 'pl':
      return { label: t('v2.board.filter_chip_pl', 'Nur meine Projekte (PL)'), predicate };
    case 'bl':
      return {
        label: t('v2.board.filter_chip_bl', 'Mein Bereich ({{segments}})', {
          segments: seg(segmente.bl),
        }),
        predicate,
      };
    case 'manager':
      return {
        label: t('v2.board.filter_chip_manager', 'Meine Verantwortung (Tag/Manager)'),
        predicate,
      };
    case 'mtb':
      return {
        label:
          segmente.mtb.length === 0
            ? t('v2.board.filter_chip_mtb', 'Bereich MTB')
            : t('v2.board.filter_chip_mtb_scoped', 'Bereich MTB ({{segments}})', {
                segments: seg(segmente.mtb),
              }),
        predicate,
      };
    case 'service':
      return {
        label:
          segmente.service.length === 0
            ? t('v2.board.filter_chip_service', 'Bereich Service')
            : t('v2.board.filter_chip_service_scoped', 'Bereich Service ({{segments}})', {
                segments: seg(segmente.service),
              }),
        predicate,
      };
    default:
      return null;
  }
}

function PersonaFilterChip({
  label,
  active,
  onToggle,
  visibleCount,
  totalCount,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
  visibleCount: number;
  totalCount: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 px-1">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md transition-colors"
        style={{
          background: active ? 'var(--primary-soft)' : 'var(--surface-2)',
          color: active ? 'var(--primary)' : 'var(--ink-3)',
          border: `1px solid ${active ? 'var(--primary)' : 'var(--line-soft)'}`,
          fontFamily: 'var(--f-mono)',
        }}
        title={
          active
            ? t('v2.board.filter_chip_tooltip_on', 'Filter wegklicken um alle Projekte zu zeigen')
            : t('v2.board.filter_chip_tooltip_off', 'Filter wieder anwenden')
        }
      >
        <span>{active ? '●' : '○'}</span>
        <span>{label}</span>
        {active && <span style={{ opacity: 0.7 }}>✕</span>}
      </button>
      <span className="text-[11px]" style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}>
        {active
          ? t('v2.board.filter_visible', '{{visible}} von {{total}}', {
              visible: visibleCount,
              total: totalCount,
            })
          : t('v2.board.filter_total', '{{total}} Projekte', { total: totalCount })}
      </span>
    </div>
  );
}

// ===========================================================================
// DueDateFilter — Toggle "Nur überfällig" + Date-Range (von–bis)
// ===========================================================================

function BoardFilterBar(props: {
  open: boolean;
  onToggleOpen: () => void;
  activeFilterCount: number;
  onResetFilters: () => void;
  search: string;
  onSearch: (v: string) => void;
  segmentFilter: string | null;
  onSegment: (v: string | null) => void;
  bereichFilter: string | null;
  onBereich: (v: string | null) => void;
  msFilter: string | null;
  onMs: (v: string | null) => void;
  needsOnly: boolean;
  onNeedsOnly: (v: boolean) => void;
  blockedOnly: boolean;
  onBlockedOnly: (v: boolean) => void;
  overdueOnly: boolean;
  onOverdueOnly: (v: boolean) => void;
  befundeAn: boolean;
  befundOnly: boolean;
  onBefundOnly: (v: boolean) => void;
  mtbFilter: string | null;
  onMtbFilter: (v: string | null) => void;
  mtbOptions: Array<{ value: string; label: string }>;
  plFilter: string[];
  onPlToggle: (id: string) => void;
  onPlClear: () => void;
  plOptions: Array<{ value: string; label: string }>;
  managerFilter: string | null;
  onManagerFilter: (v: string | null) => void;
  managerOptions: Array<{ value: string; label: string }>;
  tagFilter: string[];
  onTagToggle: (id: string) => void;
  onTagClear: () => void;
  tagOptions: Array<{ value: string; label: string }>;
  dueFrom: string;
  onDueFrom: (v: string) => void;
  dueTo: string;
  onDueTo: (v: string) => void;
  phases: Phase[];
  hiddenPhaseIds: string[];
  onTogglePhase: (phaseId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-xl border"
      style={{ background: 'var(--surface)', borderColor: 'var(--line-soft)' }}
    >
      {/* Kopf: ein-/ausklappbar + Aktiv-Indikator + „Alle Filter entfernen" */}
      <div className="flex items-center gap-2 px-4 py-2.5">
        <button
          type="button"
          onClick={props.onToggleOpen}
          className="flex-1 flex items-center gap-2 text-xs min-w-0"
          style={{ color: 'var(--ink-2)', fontFamily: 'var(--f-mono)' }}
        >
          <span
            style={{
              transform: props.open ? 'rotate(90deg)' : 'none',
              transition: 'transform 120ms',
            }}
          >
            ▸
          </span>
          <span>{t('v2.board.filters', 'Filter')}</span>
          {props.activeFilterCount > 0 && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{
                background: 'var(--primary-soft)',
                color: 'var(--primary)',
                fontFamily: 'var(--f-mono)',
              }}
            >
              {t('v2.board.filters_active', '{{n}} aktiv', { n: props.activeFilterCount })}
            </span>
          )}
          {props.hiddenPhaseIds.length > 0 && (
            <span className="text-[10px]" style={{ color: 'var(--ink-3)' }}>
              ·{' '}
              {t('v2.board.phases_hidden', '{{n}} Phasen ausgeblendet', {
                n: props.hiddenPhaseIds.length,
              })}
            </span>
          )}
        </button>
        {props.activeFilterCount > 0 && (
          <button
            type="button"
            onClick={props.onResetFilters}
            className="text-[11px] px-2 py-1 rounded-md shrink-0 transition-colors hover:bg-[var(--surface-2)]"
            style={{
              color: 'var(--danger)',
              border: '1px solid var(--line-soft)',
              fontFamily: 'var(--f-mono)',
            }}
            title={t(
              'v2.board.filters_reset_hint',
              'Alle Filter entfernen (Persona-Standardsicht bleibt erhalten)',
            )}
          >
            ✕ {t('v2.board.filters_reset', 'Alle Filter entfernen')}
          </button>
        )}
      </div>

      {props.open && (
        <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: 'var(--line-soft)' }}>
          <div className="pt-3">
            <SearchInput
              value={props.search}
              onChange={props.onSearch}
              placeholder={t('v2.atlas.search_placeholder', 'Project-Key, Kunde, Name…')}
            />
          </div>
          <ChipSelect
            label={t('v2.atlas.filter_segment', 'Segment')}
            value={props.segmentFilter}
            onChange={props.onSegment}
            allLabel={t('v2.atlas.filter_all', 'Alle')}
            options={SEGMENTS.map((s) => ({ value: s, label: s }))}
          />
          <ChipSelect
            label={t('v2.atlas.filter_bereich', 'Bereich')}
            value={props.bereichFilter}
            onChange={props.onBereich}
            allLabel={t('v2.atlas.filter_all', 'Alle')}
            options={RESPONSIBILITIES.map((r) => ({ value: r, label: r }))}
          />
          <ChipSelect
            label={t('v2.atlas.filter_ms', 'Dienstleistungsart')}
            value={props.msFilter}
            onChange={props.onMs}
            allLabel={t('v2.atlas.filter_all', 'Alle')}
            options={MS_CLASSES.map((m) => ({ value: m, label: m }))}
          />
          <ChipSelect
            label={t('v2.board.filter_mtb', 'Zuweisung MTB')}
            value={props.mtbFilter}
            onChange={props.onMtbFilter}
            allLabel={t('v2.atlas.filter_all', 'Alle')}
            options={[
              { value: 'assigned', label: t('v2.board.mtb_assigned', 'MTB zugewiesen') },
              { value: 'unassigned', label: t('v2.board.mtb_unassigned', 'nicht zugewiesen') },
            ]}
          />
          <MultiSelectPicker
            options={props.plOptions}
            value={props.plFilter}
            onToggle={props.onPlToggle}
            onClear={props.onPlClear}
            label={t('v2.board.filter_pl_person', 'Zugewiesener Projektleiter')}
            placeholder={t('v2.board.pl_pick', 'Projektleiter wählen…')}
            searchPlaceholder={t('v2.board.pl_search', 'Projektleiter suchen…')}
            clearLabel={t('v2.board.pl_clear', 'Alle entfernen')}
            countLabel={t('v2.board.pl_count', 'ausgewählt')}
            noMatchLabel={t('v2.detail.assign_no_match', 'Keine Treffer.')}
          />
          <PersonPicker
            options={props.mtbOptions}
            value={
              props.mtbFilter && props.mtbFilter !== 'assigned' && props.mtbFilter !== 'unassigned'
                ? props.mtbFilter
                : null
            }
            onChange={props.onMtbFilter}
            label={t('v2.board.filter_mtb_person', 'Konkreter MTB')}
            placeholder={t('v2.board.mtb_pick', 'Person wählen…')}
            searchPlaceholder={t('v2.board.mtb_search', 'MTB suchen…')}
            clearLabel={t('v2.board.mtb_clear', 'Filter entfernen')}
            noMatchLabel={t('v2.detail.assign_no_match', 'Keine Treffer.')}
          />
          <PersonPicker
            options={props.managerOptions}
            value={props.managerFilter}
            onChange={props.onManagerFilter}
            label={t('v2.board.filter_manager_person', 'Konkreter Manager')}
            placeholder={t('v2.board.manager_pick', 'Person wählen…')}
            searchPlaceholder={t('v2.board.manager_search', 'Manager suchen…')}
            clearLabel={t('v2.board.manager_clear', 'Filter entfernen')}
            noMatchLabel={t('v2.detail.assign_no_match', 'Keine Treffer.')}
          />
          <MultiSelectPicker
            options={props.tagOptions}
            value={props.tagFilter}
            onToggle={props.onTagToggle}
            onClear={props.onTagClear}
            label={t('v2.board.filter_tag', 'Tag')}
            placeholder={t('v2.board.tag_pick', 'Tags wählen…')}
            searchPlaceholder={t('v2.board.tag_search', 'Tag suchen…')}
            clearLabel={t('v2.board.tag_clear', 'Alle entfernen')}
            countLabel={t('v2.board.tag_count', 'ausgewählt')}
            noMatchLabel={t('v2.detail.assign_no_match', 'Keine Treffer.')}
          />
          <div className="flex flex-wrap gap-2 pt-1">
            <Toggle active={props.needsOnly} onClick={() => props.onNeedsOnly(!props.needsOnly)}>
              {t('v2.atlas.toggle_needs_only', 'Mit offenem Bedarf')}
            </Toggle>
            <Toggle
              active={props.blockedOnly}
              onClick={() => props.onBlockedOnly(!props.blockedOnly)}
            >
              {t('v2.board.toggle_blocked_only', 'Nur blockiert')}
            </Toggle>
            <Toggle
              active={props.overdueOnly}
              onClick={() => props.onOverdueOnly(!props.overdueOnly)}
            >
              {t('v2.duedate.filter_overdue_only', 'Nur überfällig')}
            </Toggle>
            {props.befundeAn && (
              <Toggle
                active={props.befundOnly}
                onClick={() => props.onBefundOnly(!props.befundOnly)}
              >
                {t('v2.board.toggle_befund_only', 'Mit Befund')}
              </Toggle>
            )}
          </div>
          <DueRangeFilter
            label={t('v2.duedate.label', 'Fälligkeit')}
            fromLabel={t('v2.duedate.filter_from', 'Fällig ab')}
            toLabel={t('v2.duedate.filter_to', 'Fällig bis')}
            from={props.dueFrom}
            to={props.dueTo}
            onFrom={props.onDueFrom}
            onTo={props.onDueTo}
          />
          {/* Phasen ein-/ausblenden (pro User persistiert) */}
          <div
            className="flex items-center gap-2 flex-wrap pt-2 border-t"
            style={{ borderColor: 'var(--line-soft)' }}
          >
            <span
              className="text-[10px] uppercase tracking-[0.14em] w-[120px] pt-2"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
            >
              {t('v2.board.phases_visible', 'Phasen')}
            </span>
            {props.phases.map((ph) => {
              const visible = !props.hiddenPhaseIds.includes(ph.phase_id);
              return (
                <Chip
                  key={ph.phase_id}
                  active={visible}
                  onClick={() => props.onTogglePhase(ph.phase_id)}
                >
                  {visible ? '◉' : '○'} {ph.name}
                </Chip>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Header
// ===========================================================================

function Header({ lens, onLens }: { lens: Lens; onLens: (l: Lens) => void }) {
  const { t } = useTranslation();
  return (
    <header className="pt-6 flex items-end justify-between gap-6 animate-fade-up">
      <div className="flex-1">
        <div
          className="text-[10.5px] uppercase tracking-[0.14em] mb-3 flex items-center gap-2"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          <span>{t('v2.board.eyebrow', 'Phasensteuerung')}</span>
          <InfoKnopf themaId="phasen" />
        </div>
        <h1
          className="font-medium leading-[1.05] tracking-tight"
          style={{
            fontFamily: 'var(--f-display)',
            color: 'var(--ink)',
            fontSize: 'clamp(22px, 2.4vw, 36px)',
          }}
        >
          {t('v2.board.headline_lead', 'Die Pipeline,')}{' '}
          <em
            className="not-italic"
            style={{ color: 'var(--primary)', fontStyle: 'italic', fontWeight: 400 }}
          >
            {t('v2.board.headline_emphasis', 'drei Sichten')}
          </em>
          {t('v2.board.headline_trail', '.')}
        </h1>
        <p className="text-sm max-w-[640px] mt-2" style={{ color: 'var(--ink-3)' }}>
          {t(
            'v2.board.subtitle',
            'Dieselben Projekte, andere Linse. Pipeline für Fluss + Engpässe, Kanban für aktive Bewegung, Matrix für jeden Verlauf parallel.',
          )}
        </p>
      </div>
      <LensSwitcher lens={lens} onLens={onLens} />
    </header>
  );
}

function LensSwitcher({ lens, onLens }: { lens: Lens; onLens: (l: Lens) => void }) {
  const { t } = useTranslation();
  const opts: Array<{ id: Lens; label: string }> = [
    { id: 'pipeline', label: t('v2.board.lens_pipeline', 'Pipeline') },
    { id: 'kanban', label: t('v2.board.lens_kanban', 'Kanban') },
    { id: 'matrix', label: t('v2.board.lens_matrix', 'Matrix') },
  ];
  return (
    <div
      className="inline-flex rounded-lg p-0.5 shrink-0"
      style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
    >
      {opts.map((o) => {
        const active = lens === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onLens(o.id)}
            className="px-3 py-1.5 text-xs rounded-md transition-colors"
            style={{
              background: active ? 'var(--surface-3)' : 'transparent',
              color: active ? 'var(--ink)' : 'var(--ink-3)',
              fontWeight: active ? 500 : 400,
              fontFamily: 'var(--f-mono)',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Linse 1 — Pipeline
// ===========================================================================

function PipelineLens({
  phases,
  byPhase,
  userNameById,
}: {
  phases: Phase[];
  byPhase: Map<string, Project[]>;
  userNameById: ReadonlyMap<string, string>;
}) {
  const { t } = useTranslation();
  const stuckByPhase = useMemo(() => {
    const m = new Map<string, number>();
    for (const ph of phases) {
      const items = byPhase.get(ph.phase_id) ?? [];
      m.set(
        ph.phase_id,
        items.filter(
          (p) => Math.floor((Date.now() - new Date(p.phase_changed_at).getTime()) / DAY_MS) >= 5,
        ).length,
      );
    }
    return m;
  }, [byPhase, phases]);

  // Bottleneck-Heuristik: Übergänge mit hoher Stuck-Quote
  const bottlenecks = useMemo(() => {
    const out: Array<{ from: Phase; to: Phase; count: number }> = [];
    for (let i = 0; i < phases.length - 1; i++) {
      const from = phases[i]!;
      const to = phases[i + 1]!;
      const count = stuckByPhase.get(from.phase_id) ?? 0;
      if (count > 0) out.push({ from, to, count });
    }
    return out.sort((a, b) => b.count - a.count).slice(0, 3);
  }, [phases, stuckByPhase]);

  const colTemplate = `repeat(${phases.length}, minmax(0, 1fr))`;

  return (
    <div className="space-y-6 animate-fade-up-d1">
      {/* Pipe-Track + Phase-Nodes */}
      <div className="relative pt-4 pb-12">
        <div
          className="absolute left-[6%] right-[6%] top-1/2 h-0.5"
          style={{
            background:
              'linear-gradient(to right, var(--line-soft), var(--primary), var(--line-soft))',
            boxShadow: '0 0 12px var(--primary-soft)',
          }}
        />
        <div className="relative grid gap-2" style={{ gridTemplateColumns: colTemplate }}>
          {phases.map((ph) => {
            const count = byPhase.get(ph.phase_id)?.length ?? 0;
            const stuck = stuckByPhase.get(ph.phase_id) ?? 0;
            return (
              <div key={ph.phase_id} className="flex flex-col items-center">
                <span
                  style={{
                    fontFamily: 'var(--f-display)',
                    fontSize: 32,
                    color: 'var(--ink)',
                    lineHeight: 1,
                  }}
                >
                  {count}
                </span>
                <span
                  className="my-2 w-3.5 h-3.5 rounded-full"
                  style={{
                    background: 'var(--primary)',
                    boxShadow: '0 0 0 4px var(--primary-soft), 0 0 12px var(--primary-soft)',
                  }}
                />
                <span
                  className="text-[10px] uppercase tracking-[0.14em] text-center"
                  style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-2)' }}
                >
                  {ph.name}
                </span>
                {stuck > 0 && (
                  <span
                    className="mt-1 px-1.5 py-0.5 rounded text-[10px]"
                    style={{
                      background: 'var(--warn-soft)',
                      color: 'var(--warn)',
                      fontFamily: 'var(--f-mono)',
                    }}
                  >
                    {stuck} stuck
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pipe-Bubbles (Projekte pro Phase) */}
      <div className="grid gap-3" style={{ gridTemplateColumns: colTemplate }}>
        {phases.map((ph) => (
          <div key={ph.phase_id} className="space-y-2">
            {(byPhase.get(ph.phase_id) ?? []).map((p) => (
              <PipeBubble key={p.project_id} project={p} userNameById={userNameById} />
            ))}
            {(byPhase.get(ph.phase_id) ?? []).length === 0 && (
              <div className="text-[10px] text-center py-2" style={{ color: 'var(--ink-4)' }}>
                —
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bottleneck-Card */}
      <div
        className="rounded-xl border p-5"
        style={{ background: 'var(--surface)', borderColor: 'var(--line-soft)' }}
      >
        <div className="flex items-baseline gap-3 mb-4">
          <h3
            style={{
              fontFamily: 'var(--f-display)',
              fontSize: 18,
              color: 'var(--ink)',
              fontWeight: 500,
            }}
          >
            {t('v2.board.bottleneck_label', 'Engpass')}
          </h3>
          <span
            className="text-[10px]"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {t('v2.board.bottleneck_hint', 'Die meisten Projekte stecken aktuell hier.')}
          </span>
        </div>
        {bottlenecks.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            —
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {bottlenecks.map((b, i) => (
              <div key={i} className="space-y-1">
                <div
                  className="text-[10px] uppercase tracking-[0.14em]"
                  style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
                >
                  {b.from.name} → {b.to.name}
                </div>
                <div
                  className="inline-flex items-center px-2 py-0.5 rounded text-[10px]"
                  style={{
                    background: 'var(--warn-soft)',
                    color: 'var(--warn)',
                    fontFamily: 'var(--f-mono)',
                  }}
                >
                  {b.count} stuck {'>'} 5d
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-2)' }}>
                  {b.count} Projekt{b.count === 1 ? '' : 'e'} im Übergang stagniert. Nachfassen.
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Projektleitung als eigene Kartenzeile (#99): Initialen-Kreis + Name. `compact`
 * (Pipeline, schmale Bubbles) zeigt den Kurznamen, der volle Name steht im Tooltip.
 * Ohne PL gedämpft „kein PL" — sichtbar, aber kein Alarm (in „Neu" fachlich normal).
 * Ist die PL-ID gesetzt, der Name aber (noch) nicht geladen, bleibt die Zeile weg,
 * statt kurz fälschlich „kein PL" zu zeigen.
 */
function LeadLine({
  leadUserId,
  userNameById,
  compact,
}: {
  leadUserId: string | null | undefined;
  userNameById: ReadonlyMap<string, string>;
  compact: boolean;
}) {
  const { t } = useTranslation();
  const size = compact ? 14 : 16;
  const name = leadUserId ? userNameById.get(leadUserId) : undefined;
  if (leadUserId && !name) return null;
  return (
    <div
      className="flex items-center gap-1.5 min-w-0"
      style={{ marginTop: compact ? 4 : 6, fontSize: compact ? 10.5 : 11.5 }}
      title={name ? `${t('v2.board.card_pl', 'Projektleitung')}: ${name}` : undefined}
    >
      <span
        aria-hidden
        className="inline-grid place-items-center shrink-0 rounded-full"
        style={{
          width: size,
          height: size,
          fontSize: compact ? 8 : 9,
          fontWeight: 600,
          color: name ? 'var(--ink-2)' : 'var(--ink-4)',
          background: name ? 'var(--bg-deeper)' : 'transparent',
          border: name ? 'none' : '1px dashed var(--ink-4)',
        }}
      >
        {name ? personInitials(name) : '?'}
      </span>
      <span
        className="truncate"
        style={{
          color: name ? 'var(--ink-2)' : 'var(--ink-4)',
          fontStyle: name ? 'normal' : 'italic',
        }}
      >
        {name ? (compact ? shortPersonName(name) : name) : t('v2.board.card_pl_none', 'kein PL')}
      </span>
    </div>
  );
}

function PipeBubble({
  project: p,
  userNameById,
}: {
  project: Project;
  userNameById: ReadonlyMap<string, string>;
}) {
  const days = Math.floor((Date.now() - new Date(p.phase_changed_at).getTime()) / DAY_MS);
  const stuck = days >= 5;
  return (
    <Link
      to={`/projects/${p.project_id}`}
      className="block px-2.5 py-2 rounded-md transition-all hover:-translate-y-0.5"
      style={{
        background: 'var(--surface)',
        border: `1px solid ${stuck ? 'var(--warn)' : 'var(--line-soft)'}`,
        textDecoration: 'none',
      }}
    >
      <div className="flex items-center gap-2">
        <span style={{ fontFamily: 'var(--f-mono)', fontSize: 10, color: 'var(--ink-3)' }}>
          {p.project_key}
        </span>
        {stuck && (
          <span
            className="ml-auto text-[10px]"
            style={{ color: 'var(--warn)', fontFamily: 'var(--f-mono)' }}
          >
            {days}d
          </span>
        )}
      </div>
      {/* Anker: Projektbezeichnung, Kunde sekundär (konsistent über alle drei Linsen) */}
      <div className="text-xs mt-0.5 truncate font-medium" style={{ color: 'var(--ink)' }}>
        {p.name}
      </div>
      <div className="text-[10px] truncate" style={{ color: 'var(--ink-3)' }}>
        {p.customer_name}
      </div>
      <LeadLine leadUserId={p.project_lead_user_id} userNameById={userNameById} compact />
      {p.due_date && (
        <div className="mt-1">
          <DueDateBadge dueDate={p.due_date} lifecycleStatus={p.lifecycle_status} />
        </div>
      )}
    </Link>
  );
}

// ===========================================================================
// Linse 2 — Kanban
// ===========================================================================

function KanbanLens({
  phases,
  byPhase,
  gate,
  userNameById,
}: {
  phases: Phase[];
  byPhase: Map<string, Project[]>;
  /** #61: gated Uebergang aus GET /v1/phases. Fehlt er, wird nichts gesperrt. */
  gate?: { from_phase: string; to_phase: string } | undefined;
  userNameById: ReadonlyMap<string, string>;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useSession();
  const mayEnterGatedPhase = user ? canEnterGatedPhase(user) : false;
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [pflicht, setPflicht] = useState<{ projectId: string; toPhaseId: string } | null>(null);
  const befundUebersicht = useBefundUebersicht();

  // #117: Blockierte Projekte wechseln die Phase nicht. Derselbe Hinweis beim Drop einer
  // blockierten Karte und bei einer 409 `project_blocked` (Block inzwischen anderswo gesetzt).
  const zeigeBlockHinweis = (reason?: string | null) =>
    toast.error(
      t('v2.board.move_blocked_project', 'Projekt ist blockiert'),
      reason
        ? t('v2.board.move_blocked_project_hint_reason', 'Erst Block lösen. Grund: {{reason}}', {
            reason,
          })
        : t('v2.board.move_blocked_project_hint', 'Erst Block lösen, dann die Phase wechseln.'),
    );

  const movePhase = useMutation({
    mutationFn: ({ projectId, toPhaseId }: { projectId: string; toPhaseId: string }) =>
      api.movePhase(projectId, toPhaseId),
    onSuccess: () => {
      toast.success(t('v2.board.phase_moved', 'Phase gewechselt'));
      void queryClient.invalidateQueries({ queryKey: ['v2-board-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['v2-board-projects'] });
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        (err.payload as { error?: string } | null)?.error === 'project_blocked'
      ) {
        zeigeBlockHinweis();
        return;
      }
      if (err instanceof ApiError && err.isHardStop()) {
        // A8: Fehlen nur Felder zum BETRETEN der Zielphase, lassen sie sich im Dialog nachtragen.
        // Fehlen Felder zum Verlassen der alten Phase, bleibt es beim Toast.
        const nurEintritt =
          err.payload.errors.length > 0 && err.payload.errors.every((v) => v.direction === 'enter');
        if (nurEintritt) {
          setPflicht(vars);
          return;
        }
        const lines = err.payload.errors.map(
          (v) =>
            `• ${prettyField(v.field)} (${v.direction === 'leave' ? 'Pflicht zum Verlassen' : 'Pflicht zum Betreten'})`,
        );
        toast.error(
          t('v2.board.move_blocked', 'Phasenwechsel blockiert: {{from}} → {{to}}', {
            from: err.payload.from_phase ?? '?',
            to: err.payload.to_phase,
          }),
          lines.join('\n'),
        );
      } else {
        toast.error(
          t('v2.board.move_failed', 'Phasenwechsel fehlgeschlagen'),
          err instanceof Error ? err.message : 'Unbekannter Fehler',
        );
      }
    },
  });

  // Skalierung (F4, Deckel seit #50 bei 420px): Spalten teilen die Breite, aber
  // gedeckelt → kein Zerlaufen auf breitem Screen (Karten bleiben lesbar),
  // linksbündig statt gestreckt. Mindestbreite 210px + overflow-x-auto → auf
  // schmalen Schirmen bleiben die Spalten lesbar und die Sicht scrollt seitlich.
  // Beide Grenzen stehen in phase-board-layout.ts und sind dort getestet.
  return (
    <div className="animate-fade-up-d1 -mx-2 px-2 pb-4 overflow-x-auto">
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: kanbanColumnTemplate(phases.length),
          justifyContent: 'start',
        }}
      >
        {phases.map((ph) => {
          const items = byPhase.get(ph.phase_id) ?? [];
          const isDropHere = dropTarget === ph.phase_id;
          return (
            <div
              key={ph.phase_id}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(DND_MIME)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dropTarget !== ph.phase_id) setDropTarget(ph.phase_id);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                if (dropTarget === ph.phase_id) setDropTarget(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const projectId = e.dataTransfer.getData(DND_MIME);
                setDropTarget(null);
                setDragId(null);
                if (!projectId) return;
                const project = items
                  .concat(...byPhase.values())
                  .find((p) => p.project_id === projectId);
                if (project && project.phase_id === ph.phase_id) return; // no-op
                // #117: blockierte Karte → Hinweis statt Phasenwechsel (der Server antwortete 409).
                if (project?.is_blocked) {
                  zeigeBlockHinweis(project.blocked_reason);
                  return;
                }
                // #61: Gated Uebergang gar nicht erst absetzen — der Server
                // wuerde mit 403 antworten, die Karte spraenge sichtbar zurueck.
                const fromPhaseName = phases.find((x) => x.phase_id === project?.phase_id)?.name;
                if (
                  gate &&
                  !mayEnterGatedPhase &&
                  fromPhaseName === gate.from_phase &&
                  ph.name === gate.to_phase
                ) {
                  toast.error(
                    t('v2.board.move_blocked_gate', 'Phasenwechsel nicht erlaubt'),
                    t(
                      'v2.board.move_blocked_gate_hint',
                      'Der Wechsel von {{from}} nach {{to}} ist der Kaufm. Leitung und Admins vorbehalten.',
                      { from: gate.from_phase, to: gate.to_phase },
                    ),
                  );
                  return;
                }
                movePhase.mutate({ projectId, toPhaseId: ph.phase_id });
              }}
              className="rounded-xl border p-3 min-w-0 transition-colors"
              style={{
                background: isDropHere ? 'var(--primary-soft)' : 'var(--surface)',
                borderColor: isDropHere ? 'var(--primary)' : 'var(--line-soft)',
                maxHeight: 'calc(100vh - 240px)',
              }}
            >
              <div className="flex items-baseline justify-between mb-3 px-1">
                <h3 style={{ fontFamily: 'var(--f-display)', fontSize: 16, color: 'var(--ink)' }}>
                  {ph.name}
                </h3>
                <span style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                  {items.length}
                </span>
              </div>
              <ul
                className="space-y-2 overflow-y-auto"
                style={{ maxHeight: 'calc(100vh - 320px)' }}
              >
                {items.map((p) => (
                  <KanbanCard
                    key={p.project_id}
                    project={p}
                    userNameById={userNameById}
                    dragging={dragId === p.project_id}
                    onDragStart={() => setDragId(p.project_id)}
                    onDragEnd={() => {
                      setDragId(null);
                      setDropTarget(null);
                    }}
                    befund={befundUebersicht.get(p.project_id)}
                  />
                ))}
                {items.length === 0 && (
                  <li
                    className="text-[11px] text-center py-3 rounded-md border border-dashed"
                    style={{ color: 'var(--ink-4)', borderColor: 'var(--line-soft)' }}
                  >
                    {isDropHere
                      ? t('v2.board.drop_here', 'hier ablegen')
                      : t('v2.board.empty_column', 'leer')}
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
      {pflicht && (
        <BoardPflichtfeldDialog
          projectId={pflicht.projectId}
          toPhaseId={pflicht.toPhaseId}
          phases={phases}
          gate={gate}
          onClose={() => setPflicht(null)}
        />
      )}
      <p className="mt-3 text-xs" style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}>
        {t(
          'v2.board.dnd_hint',
          'Tipp: Karte ziehen, in andere Spalte fallen lassen. Hard-Stop-Verletzung → Toast + Snap-Back.',
        )}
      </p>
    </div>
  );
}

function KanbanCard({
  project: p,
  userNameById,
  dragging,
  onDragStart,
  onDragEnd,
  befund,
}: {
  project: Project;
  userNameById: ReadonlyMap<string, string>;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  /** PV-052: offene Befunde des Projekts (nur mit Flag `befunde`). */
  befund?: BefundUebersichtEintrag | undefined;
}) {
  const { t, i18n } = useTranslation();
  const days = Math.floor((Date.now() - new Date(p.phase_changed_at).getTime()) / DAY_MS);
  const todayIso = new Date().toISOString().slice(0, 10);
  const overdue = p.lifecycle_status === 'active' && !!p.due_date && p.due_date < todayIso;
  // Akzent-Streifen links: Blockade dominiert Überfälligkeit, sonst keiner.
  const strip = p.is_blocked ? 'var(--danger)' : overdue ? 'var(--warn)' : null;
  const bereich = bereichColors(p.responsibility ?? null);
  const tags = p.tags ?? [];
  const dueLabel = p.due_date
    ? new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: '2-digit' }).format(
        new Date(p.due_date),
      )
    : null;
  const mono = 'var(--f-mono)';
  // Nur aktive Flags werden zu Punkten — leise Karte statt Badge-Salat.
  // #117: Der Blockgrund steht als Tooltip am Chip „blockiert" (spart den Weg ins Audit-Log).
  const signals = [
    p.is_blocked && {
      c: 'var(--danger)',
      t: t('v2.board.card_blocked', 'blockiert'),
      title: p.blocked_reason ?? undefined,
    },
    p.qs_with_bl_open && { c: 'var(--primary)', t: t('v2.board.card_qs_bl', 'QS-BL') },
    p.staffing_needed && { c: 'var(--warn)', t: t('v2.board.card_staffing', 'Staffing') },
  ].filter(Boolean) as { c: string; t: string; title?: string }[];

  return (
    <li
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(DND_MIME, p.project_id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      style={{ opacity: dragging ? 0.4 : 1, position: 'relative' }}
    >
      <Link
        to={`/projects/${p.project_id}`}
        onClick={(e) => {
          // Klick während Drag passiert nicht — aber Sicherheit gegen versehentlichen Klick nach Drag.
          if (dragging) e.preventDefault();
        }}
        className="block relative overflow-hidden transition-all hover:translate-x-0.5"
        style={{
          padding: strip ? '11px 12px 11px 14px' : '11px 12px',
          borderRadius: 7,
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          textDecoration: 'none',
          cursor: dragging ? 'grabbing' : 'grab',
        }}
      >
        {strip && (
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              background: strip,
            }}
          />
        )}

        {/* Meta: ID kräftig + Segment als Neutral-Chip · Bereich als Punkt rechts */}
        <div className="flex items-center gap-1.5 mb-1.5">
          <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 500, color: 'var(--ink-2)' }}>
            {p.project_key}
          </span>
          <span
            style={{
              fontFamily: mono,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: 'var(--ink)',
              background: 'var(--bg-deeper)',
              borderRadius: 4,
              padding: '1.5px 5px',
            }}
          >
            {p.segment}
          </span>
          <span
            className="ml-auto flex items-center gap-1.5"
            style={befund ? { marginRight: 22 } : undefined}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: bereich.fg }} />
            <span style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
              {p.responsibility ?? t('v2.bereich.open', 'offen')}
            </span>
          </span>
        </div>

        {/* Anker: Projektbezeichnung fett, Kunde sekundär (konsistent über alle drei Linsen) */}
        <div
          className="truncate"
          style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25, color: 'var(--ink)' }}
        >
          {p.name}
        </div>
        <div className="truncate" style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
          {p.customer_name}
        </div>

        {/* #99: Projektleitung als eigene Zeile, vor den Tags (Wer/Was vor Klassifikation) */}
        <LeadLine leadUserId={p.project_lead_user_id} userNameById={userNameById} compact={false} />

        {/* Tags: Punkt + neutrales Label */}
        {tags.length > 0 && (
          <div className="flex items-center gap-2.5 mt-2 flex-wrap">
            {tags.slice(0, 2).map((tg) => (
              <span key={tg.tag_id} className="inline-flex items-center gap-1.5">
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 2,
                    background: tg.color ?? 'var(--ink-4)',
                  }}
                />
                <span style={{ fontSize: 11, color: 'var(--ink-2)' }}>{tg.name}</span>
              </span>
            ))}
            {tags.length > 2 && (
              <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>+{tags.length - 2}</span>
            )}
          </div>
        )}

        {/* Signalzeile: nur aktive Flags als gedämpfte Punkte + Fälligkeit rechts */}
        {(signals.length > 0 || dueLabel) && (
          <div
            className="flex items-center gap-3 mt-2 pt-2"
            style={{ borderTop: '1px solid var(--bg-deeper)' }}
          >
            {signals.map((s) => (
              <span key={s.t} className="inline-flex items-center gap-1.5" title={s.title}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.c }} />
                <span style={{ fontSize: 10.5, color: 'var(--ink-2)' }}>{s.t}</span>
              </span>
            ))}
            {days >= 5 && (
              <span style={{ fontFamily: mono, fontSize: 10.5, color: 'var(--ink-4)' }}>
                {days}d
              </span>
            )}
            {dueLabel && (
              <span
                className="ml-auto inline-flex items-center gap-1.5"
                style={{
                  fontFamily: mono,
                  fontSize: 10.5,
                  color: overdue ? 'var(--danger)' : 'var(--ink-3)',
                }}
              >
                {overdue && (
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: 'var(--danger)',
                    }}
                  />
                )}
                {dueLabel}
              </span>
            )}
          </div>
        )}
      </Link>
      {/* Außerhalb des Links: ein Klick darf nie ins Projekt navigieren (A10). */}
      {befund && (
        <span style={{ position: 'absolute', top: 9, right: 9 }}>
          <BefundMarker projectId={p.project_id} eintrag={befund} />
        </span>
      )}
    </li>
  );
}

const FIELD_LABELS_BOARD: Record<string, string> = {
  project_lead_user_id: 'Project Lead',
  manager_user_id: 'Manager',
  mtb_user_id: 'MTB',
  responsibility: 'Bereich',
  'handover_ready_fields.restbudget': 'Restbudget',
  'handover_ready_fields.folgethemen': 'Folgethemen',
  'handover_ready_fields.debriefing': 'Debriefing',
  'handover_ready_fields.uebergabe_an_service': 'Übergabe an Service',
  'intake_handover_fields.presales_notes': 'Presales-Notizen',
  'intake_handover_fields.angebot_link': 'Angebots-Link',
  'intake_handover_fields.angebot_besonderheiten': 'Angebots-Besonderheiten',
  'intake_handover_fields.intake_meeting_notes': 'Intake-Meeting-Notizen',
};

function prettyField(path: string): string {
  return FIELD_LABELS_BOARD[path] ?? path;
}

// ===========================================================================
// Linse 3 — Matrix
// ===========================================================================

function MatrixLens({ phases, projects }: { phases: Phase[]; projects: Project[] }) {
  // Pro Projekt finden wir den Phase-Index. Dann: alles vor i = done, i = current, danach = leer.
  const phaseIdx = new Map<string, number>();
  phases.forEach((ph, i) => phaseIdx.set(ph.phase_id, i));

  const rows = projects
    .map((p) => ({ project: p, idx: phaseIdx.get(p.phase_id) ?? -1 }))
    .filter((r) => r.idx >= 0)
    .sort((a, b) => b.idx - a.idx); // Fortgeschrittenste oben

  return (
    <div className="animate-fade-up-d1 overflow-x-auto -mx-2 px-2">
      <div
        className="rounded-xl border overflow-hidden"
        style={{ borderColor: 'var(--line-soft)', background: 'var(--surface)' }}
      >
        {/* Header */}
        <div
          className="grid items-center"
          style={{
            gridTemplateColumns: matrixColumnTemplate(phases.length),
            background: 'var(--surface-2)',
          }}
        >
          <div
            className="px-4 py-3 text-[10px] uppercase tracking-[0.14em]"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            Projekt
          </div>
          {phases.map((ph) => (
            <div
              key={ph.phase_id}
              className="px-2 py-3 text-center text-[10px] uppercase tracking-[0.1em]"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
            >
              {ph.name}
            </div>
          ))}
        </div>

        {rows.map(({ project: p, idx }) => {
          const days = Math.floor((Date.now() - new Date(p.phase_changed_at).getTime()) / DAY_MS);
          return (
            <Link
              key={p.project_id}
              to={`/projects/${p.project_id}`}
              className="grid items-stretch hover:bg-[var(--surface-2)] transition-colors border-t"
              style={{
                gridTemplateColumns: matrixColumnTemplate(phases.length),
                borderColor: 'var(--line-soft)',
                textDecoration: 'none',
              }}
            >
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    style={{ fontFamily: 'var(--f-mono)', fontSize: 10.5, color: 'var(--ink-3)' }}
                  >
                    {p.project_key}
                  </span>
                  <span
                    style={{ fontFamily: 'var(--f-mono)', fontSize: 10.5, color: 'var(--ink-3)' }}
                  >
                    · {p.segment}
                  </span>
                  <span className="ml-auto">
                    <DueDateBadge dueDate={p.due_date} lifecycleStatus={p.lifecycle_status} />
                  </span>
                </div>
                <div className="text-[13px] font-medium truncate" style={{ color: 'var(--ink)' }}>
                  {p.name}
                </div>
                <div className="text-[11px] truncate" style={{ color: 'var(--ink-3)' }}>
                  {p.customer_name}
                </div>
              </div>
              {phases.map((_, i) => {
                const state = i < idx ? 'done' : i === idx ? 'current' : 'future';
                const heat =
                  state === 'done'
                    ? 'var(--primary-soft)'
                    : state === 'current'
                      ? 'var(--primary-soft)'
                      : 'transparent';
                return (
                  <div
                    key={i}
                    className="flex items-center justify-center py-3 relative"
                    style={{ background: heat, borderLeft: '1px solid var(--line-soft)' }}
                  >
                    {state === 'done' && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{ color: 'var(--primary)' }}
                      >
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    )}
                    {state === 'current' && (
                      <div className="flex flex-col items-center gap-0.5">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{
                            background: 'var(--primary)',
                            animation: 'pulse-soft 2s ease-in-out infinite',
                          }}
                        />
                        <span
                          className="text-[10px]"
                          style={{
                            fontFamily: 'var(--f-mono)',
                            color: days >= 5 ? 'var(--warn)' : 'var(--primary)',
                          }}
                        >
                          {days >= 5 ? `${days}d ◐` : 'aktiv'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </Link>
          );
        })}
      </div>

      <p className="mt-3 text-xs" style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}>
        gefüllte Zellen = durchlaufene Phasen · gelb = Tage stuck im aktuellen Schritt
      </p>
    </div>
  );
}
